import React, { useEffect, useRef } from 'react';

interface TabsProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: Array<{ value: T; label: React.ReactNode; count?: number; disabled?: boolean }>;
  size?: 'sm' | 'md';
  variant?: 'segmented' | 'underlined';
  fill?: boolean;
  style?: React.CSSProperties;
  ariaLabel?: string;
}

function Tabs<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
  variant = 'segmented',
  fill,
  style,
  ariaLabel,
}: TabsProps<T>) {
  // The underlined bar scrolls sideways inside itself once its tabs outgrow
  // the frame (4+ tabs on a phone). Keep the selected tab in view by moving
  // only the tablist: Element.scrollIntoView would also scroll the dashboard
  // frame around it.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = listRef.current;
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const tab = list.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!tab) return;
    const listBox = list.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    if (tabBox.left < listBox.left) {
      list.scrollLeft -= listBox.left - tabBox.left;
    } else if (tabBox.right > listBox.right) {
      // A tab wider than the bar lines up on its left edge.
      list.scrollLeft += Math.min(tabBox.right - listBox.right, tabBox.left - listBox.left);
    }
  }, [value]);

  if (variant === 'underlined') {
    return (
      <div
        ref={listRef}
        role="tablist"
        aria-label={ariaLabel}
        className="cg-tabs-scroll"
        style={{
          display: 'flex',
          gap: 4,
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          // The rule is an inset shadow, not a border: a scroll container
          // clips at its padding box, which would cut the active tab's 2px
          // underline down to 1px where it overlaps the rule.
          boxShadow: 'inset 0 -1px 0 var(--border-1)',
          ...style,
        }}
      >
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              role="tab"
              aria-selected={active}
              disabled={o.disabled}
              onClick={() => !o.disabled && onChange(o.value)}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
                padding: size === 'sm' ? '8px 10px' : '10px 14px',
                fontSize: size === 'sm' ? 12.5 : 13.5,
                fontWeight: active ? 600 : 500,
                color: active ? 'var(--fg-1)' : 'var(--fg-3)',
                cursor: o.disabled ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-sans)',
                flexShrink: 0,
                // Inset so the scrolling tablist doesn't clip the focus ring.
                outlineOffset: -2,
                opacity: o.disabled ? 0.5 : 1,
                whiteSpace: 'nowrap',
                transition: 'color 160ms var(--ease-standard), border-color 160ms var(--ease-standard)',
              }}
            >
              {o.label}
              {typeof o.count === 'number' && (
                <span
                  style={{
                    marginLeft: 6,
                    background: active ? 'var(--accent-soft)' : 'var(--cg-neutral-100)',
                    color: active ? 'var(--accent-soft-fg)' : 'var(--fg-3)',
                    padding: '1px 7px',
                    borderRadius: 'var(--radius-pill)',
                    fontSize: 11,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {o.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        display: fill ? 'flex' : 'inline-flex',
        width: fill ? '100%' : undefined,
        background: 'rgba(255,255,255,0.7)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--radius-md)',
        padding: 3,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        gap: 2,
        ...style,
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            role="tab"
            aria-selected={active}
            disabled={o.disabled}
            onClick={() => !o.disabled && onChange(o.value)}
            style={{
              flex: fill ? 1 : undefined,
              background: active ? '#fff' : 'transparent',
              border: 'none',
              padding: size === 'sm' ? '5px 11px' : '6px 14px',
              fontSize: size === 'sm' ? 12.5 : 13,
              fontWeight: active ? 600 : 500,
              color: active ? 'var(--fg-1)' : 'var(--fg-3)',
              borderRadius: 'var(--radius-sm)',
              cursor: o.disabled ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-sans)',
              boxShadow: active ? 'var(--shadow-xs)' : 'none',
              opacity: o.disabled ? 0.5 : 1,
              whiteSpace: 'nowrap',
              transition: 'background 160ms var(--ease-standard), color 160ms var(--ease-standard)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {o.label}
            {typeof o.count === 'number' && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  color: active ? 'var(--fg-3)' : 'var(--fg-4)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
